'use client';

import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ProductPpcBenefits from '@/components/sections/Products/ProductPpcBenefits';
import ProductStandaloneFaqSection from '@/components/sections/Products/ProductStandaloneFaqSection';
import { useSalesCallbackOptional } from '@/components/sales-callback/SalesCallbackProvider';
import { ImageType, ProductInnerButton, ProductInnerListItem } from '@/types/product';

interface ProductFaqItem {
  id: number;
  faqTitle: string;
  faqContent: string;
}

interface ProductBenefitsFaqTabsProps {
  locale: string;
  benefits?: {
    heading?: string;
    subHeading?: string;
    media?: ImageType;
    smallMedia?: ImageType;
    benefitsList?: ProductInnerListItem[];
  };
  faq?: {
    heading?: string;
    subheading?: string | null;
    faqImage?: ImageType;
    faqs?: ProductFaqItem[];
    helpText?: string;
    connectButton?: ProductInnerButton;
  };
}

type ProductTab = 'benefits' | 'faq';

const tabs: Array<{ id: ProductTab; label: string }> = [
  { id: 'benefits', label: 'Benefits' },
  { id: 'faq', label: 'FAQ' },
];

const TAB_PIN_OFFSET = 8;

type TabPinState = {
  isFixed: boolean;
  fixTabs: () => void;
  releaseTabs: () => void;
  updatePinState: () => void;
};

function getSiteHeaderHeight() {
  if (typeof window === 'undefined') return 88;

  const header = document.getElementById('site-header');
  if (header) {
    return header.getBoundingClientRect().height;
  }

  const cssValue = getComputedStyle(document.documentElement)
    .getPropertyValue('--site-header-height')
    .trim();
  const headerHeight = Number.parseFloat(cssValue);

  return Number.isFinite(headerHeight) ? headerHeight : 88;
}

function getTabPinTop() {
  return getSiteHeaderHeight() + TAB_PIN_OFFSET;
}

/** Whether the tab bar should currently be pinned (sentinel past the pin line
 *  and the section still tall enough to keep the pinned bar within it). */
function shouldPinTabs(section: HTMLElement, sentinel: HTMLElement, tabPin: HTMLElement): boolean {
  const pinTop = getTabPinTop();
  const sentinelRect = sentinel.getBoundingClientRect();
  const sectionRect = section.getBoundingClientRect();
  const tabHeight = tabPin.offsetHeight || sentinel.offsetHeight || 0;

  const sentinelPastPinLine = sentinelRect.top <= pinTop + 1;
  const sectionStillVisible = sectionRect.bottom > pinTop + tabHeight + 1;

  return sentinelPastPinLine && sectionStillVisible;
}

/** Pin the tab bar to the viewport by relocating it to <body> as fixed. */
function fixTabs(
  sentinel: HTMLElement,
  tabPin: HTMLElement,
  pinStateRef: { current: TabPinState },
) {
  const pinTop = getTabPinTop();

  sentinel.style.height = `${tabPin.offsetHeight}px`;

  if (tabPin.parentElement !== document.body) {
    document.body.appendChild(tabPin);
  }

  tabPin.classList.add('tabs-pinned');

  Object.assign(tabPin.style, {
    position: 'fixed',
    top: `${pinTop}px`,
    left: '0',
    right: '0',
    width: '100%',
    zIndex: '90',
    pointerEvents: 'none',
  });

  pinStateRef.current.isFixed = true;
}

/** Undo {@link fixTabs}: move the tab bar back into the sentinel and clear styles. */
function releaseTabs(
  sentinel: HTMLElement,
  tabPin: HTMLElement,
  pinStateRef: { current: TabPinState },
) {
  if (!pinStateRef.current.isFixed && tabPin.parentElement !== document.body) {
    return;
  }

  pinStateRef.current.isFixed = false;
  sentinel.style.height = '';
  tabPin.classList.remove('tabs-pinned');

  if (tabPin.parentElement === document.body) {
    sentinel.appendChild(tabPin);
  }

  tabPin.style.position = '';
  tabPin.style.top = '';
  tabPin.style.left = '';
  tabPin.style.right = '';
  tabPin.style.width = '';
  tabPin.style.zIndex = '';
  tabPin.style.pointerEvents = '';
}

export default function ProductBenefitsFaqTabs({
  locale,
  benefits,
  faq,
}: ProductBenefitsFaqTabsProps) {
  const hasBenefits = Boolean(benefits?.benefitsList?.length);
  const hasFaqs = Boolean(faq?.faqs?.length);
  const availableTabs = useMemo(
    () =>
      tabs.filter((tab) => (tab.id === 'benefits' && hasBenefits) || (tab.id === 'faq' && hasFaqs)),
    [hasBenefits, hasFaqs],
  );
  const [activeTab, setActiveTab] = useState<ProductTab>(availableTabs[0]?.id ?? 'benefits');
  const salesCallback = useSalesCallbackOptional();
  const heading = benefits?.heading || faq?.heading;
  const subHeading = benefits?.subHeading || faq?.subheading;

  const sectionRef = useRef<HTMLElement>(null);
  const tabSentinelRef = useRef<HTMLDivElement>(null);
  const tabPinRef = useRef<HTMLDivElement>(null);
  const pinStateRef = useRef<TabPinState>({
    isFixed: false,
    fixTabs: () => undefined,
    releaseTabs: () => undefined,
    updatePinState: () => undefined,
  });

  useGSAP(
    () => {
      gsap.registerPlugin(ScrollTrigger);

      const section = sectionRef.current;
      const sentinel = tabSentinelRef.current;
      const tabPin = tabPinRef.current;

      if (!section || !sentinel || !tabPin) {
        return undefined;
      }

      let scrollRaf = 0;

      const updatePinState = () => {
        if (shouldPinTabs(section, sentinel, tabPin)) {
          fixTabs(sentinel, tabPin, pinStateRef);
        } else {
          releaseTabs(sentinel, tabPin, pinStateRef);
        }
      };

      pinStateRef.current.fixTabs = () => fixTabs(sentinel, tabPin, pinStateRef);
      pinStateRef.current.releaseTabs = () => releaseTabs(sentinel, tabPin, pinStateRef);
      pinStateRef.current.updatePinState = updatePinState;

      const syncPinLayout = () => {
        ScrollTrigger.refresh();
        updatePinState();
      };

      const onScroll = () => {
        window.cancelAnimationFrame(scrollRaf);
        scrollRaf = window.requestAnimationFrame(updatePinState);
      };

      const pinTrigger = ScrollTrigger.create({
        trigger: section,
        start: 'top bottom',
        end: 'bottom top',
        invalidateOnRefresh: true,
        onUpdate: updatePinState,
        onRefresh: updatePinState,
      });

      const header = document.getElementById('site-header');
      const headerObserver = header
        ? new ResizeObserver(() => {
            syncPinLayout();
          })
        : null;

      if (header && headerObserver) {
        headerObserver.observe(header);
      }

      const onScrollTriggerScroll = () => {
        if (!pinStateRef.current.isFixed && tabPin.parentElement !== document.body) {
          return;
        }

        window.cancelAnimationFrame(scrollRaf);
        scrollRaf = window.requestAnimationFrame(updatePinState);
      };

      gsap.ticker.add(onScrollTriggerScroll);
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', syncPinLayout);

      const refreshId = window.requestAnimationFrame(syncPinLayout);

      return () => {
        window.cancelAnimationFrame(refreshId);
        window.cancelAnimationFrame(scrollRaf);
        gsap.ticker.remove(onScrollTriggerScroll);
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', syncPinLayout);
        headerObserver?.disconnect();
        pinTrigger.kill();
        releaseTabs(sentinel, tabPin, pinStateRef);
      };
    },
    { scope: sectionRef, dependencies: [availableTabs.length] },
  );

  useLayoutEffect(() => {
    const syncAfterTabChange = () => {
      ScrollTrigger.refresh();
      pinStateRef.current.updatePinState();
    };

    const frame = window.requestAnimationFrame(syncAfterTabChange);
    const timer = window.setTimeout(syncAfterTabChange, 120);
    const lateTimer = window.setTimeout(syncAfterTabChange, 400);

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(timer);
      window.clearTimeout(lateTimer);
    };
  }, [activeTab]);

  return (
    <section ref={sectionRef} className="bg-[#f0f0f0]" aria-label="Product details">
      <div className="container mx-auto px-4 sm:px-6 lg:px-0">
        <div className="mx-auto mb-8 max-w-3xl text-center md:mb-10">
          {heading ? (
            <h2 className="text-3xl font-semibold tracking-tight text-[#0c0c0b] md:text-4xl lg:text-[2.5rem] lg:leading-tight">
              {heading}
            </h2>
          ) : null}
          {subHeading && (
            <div
              className="prose prose-gray mx-auto mt-4 max-w-2xl text-base leading-relaxed text-[#0c0c0b]/70 md:text-lg"
              dangerouslySetInnerHTML={{ __html: subHeading }}
            />
          )}
        </div>

        <div
          ref={tabSentinelRef}
          className="relative z-40 -mx-4 px-4 py-3 sm:-mx-6 sm:px-6 lg:mx-0 lg:px-0"
        >
          <div ref={tabPinRef} className="group/tabpin relative w-full">
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 -top-5 -bottom-5 z-0 opacity-0 transition-opacity duration-300 sm:-top-6 sm:-bottom-6 md:-top-7 md:-bottom-7 group-[.tabs-pinned]/tabpin:opacity-100"
            >
              <div className="h-full w-full bg-[linear-gradient(to_bottom,transparent_0%,#f0f0f0_12%,#f0f0f0_88%,transparent_100%)]" />
            </div>

            <div className="relative z-10 mx-auto w-full max-w-md px-4 pointer-events-auto sm:px-6 lg:px-0">
              <div
                role="tablist"
                aria-label="Product benefits and FAQ"
                className="flex w-full rounded-full border border-[#0c0c0b]/10 bg-white/90 p-1 shadow-[0_8px_24px_rgba(12,12,11,0.08)] backdrop-blur-md"
              >
                {availableTabs.map((tab) => {
                  const isActive = activeTab === tab.id;

                  return (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      id={`product-${tab.id}-tab`}
                      aria-selected={isActive}
                      aria-controls={`product-${tab.id}-panel`}
                      className={`relative flex-1 rounded-full px-5 py-3 text-sm font-semibold uppercase tracking-[0.18em] transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600 focus-visible:ring-offset-2 focus-visible:ring-offset-[#edebe4] sm:text-[13px] ${
                        isActive
                          ? 'bg-red-600 text-white shadow-[0_12px_28px_rgba(220,38,38,0.28)]'
                          : 'text-[#0c0c0b]/65 hover:text-[#0c0c0b]'
                      }`}
                      onClick={() => {
                        setActiveTab(tab.id);
                        if (tab.id === 'faq') {
                          salesCallback?.showTab();
                        }
                      }}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div
          id={`product-${activeTab}-panel`}
          role="tabpanel"
          aria-labelledby={`product-${activeTab}-tab`}
          className="mt-6 pb-0 sm:mt-8"
        >
          {activeTab === 'benefits' ? (
            <ProductPpcBenefits
              heading={benefits?.heading}
              subHeading={benefits?.subHeading}
              media={benefits?.media}
              detailMedia={benefits?.smallMedia}
              benefitsList={benefits?.benefitsList}
              showHeader={false}
            />
          ) : (
            <ProductStandaloneFaqSection
              locale={locale}
              heading={faq?.heading}
              subheading={faq?.subheading}
              faqs={faq?.faqs}
              media={faq?.faqImage ?? benefits?.media}
              helpText={faq?.helpText}
              connectButton={faq?.connectButton}
              showHeader={false}
            />
          )}
        </div>
      </div>
    </section>
  );
}
